/**
 * TreadmillExportContext.tsx
 *
 * Global background export manager for Treadmills.
 * Allows teachers to start exporting a Treadmill to MP4 video in the background,
 * close the Treadmill editor/preview modal, and continue working freely across the app!
 */
import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { exportTreadmillToVideo, type ExportProgress } from './treadmillVideoExporter';
import type { TreadmillItem } from '@/api/treadmills.api';

interface StartExportOptions {
  items: TreadmillItem[];
  treadmillName?: string;
}

interface TreadmillExportContextValue {
  isExporting: boolean;
  treadmillName: string;
  progress: ExportProgress | null;
  startExport: (options: StartExportOptions) => void;
  cancelExport: () => void;
}

const TreadmillExportContext = createContext<TreadmillExportContextValue | null>(null);

export function TreadmillExportProvider({ children }: { children: React.ReactNode }) {
  const [isExporting, setIsExporting] = useState(false);
  const [treadmillName, setTreadmillName] = useState('Тредмил');
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const cancelExport = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsExporting(false);
    setProgress(null);
    toast('Экспорт видео отменён', { icon: 'ℹ️' });
  }, []);

  const startExport = useCallback(async (options: StartExportOptions) => {
    if (isExporting) {
      toast.error('Уже идет экспорт другого видео! Пожалуйста, подождите.');
      return;
    }

    const { items, treadmillName: name = 'Тредмил' } = options;
    if (!items || items.length === 0) {
      toast.error('Нет слайдов для экспорта');
      return;
    }

    const nameToUse = name && name !== 'Тредмил' ? name : 'Тредмил';
    setTreadmillName(nameToUse);
    setIsExporting(true);
    setIsMinimized(false);
    setProgress({ currentSlide: 0, totalSlides: items.length, percent: 0, statusText: 'Инициализация...' });

    const controller = new AbortController();
    abortControllerRef.current = controller;

    toast.success(`🎥 Запущен экспорт в MP4: «${nameToUse}»`, { duration: 4000 });

    try {
      await exportTreadmillToVideo({
        items,
        treadmillName: nameToUse,
        onProgress: (p) => setProgress(p),
        signal: controller.signal,
      });

      toast.success(`🎉 Видео «${nameToUse}.mp4» успешно скачано!`, { duration: 6000 });
    } catch (err: any) {
      if (err?.message !== 'Экспорт отменён пользователем') {
        toast.error(`Ошибка экспорта видео: ${err?.message || 'неизвестная ошибка'}`);
      }
    } finally {
      setIsExporting(false);
      setProgress(null);
      abortControllerRef.current = null;
    }
  }, [isExporting]);

  return (
    <TreadmillExportContext.Provider
      value={{
        isExporting,
        treadmillName,
        progress,
        startExport,
        cancelExport,
      }}
    >
      {children}

      {/* ── Global Floating Background Export Widget ── */}
      {isExporting && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 99999,
            width: isMinimized ? 220 : 340,
            background: '#0f172a',
            border: '1.5px solid rgba(99,102,241,0.5)',
            borderRadius: 16,
            padding: isMinimized ? '10px 14px' : '16px 20px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.6)',
            color: '#fff',
            fontFamily: 'var(--font-sans, system-ui, sans-serif)',
            transition: 'all 0.25s ease-in-out',
            backdropFilter: 'blur(12px)',
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
              <span style={{ fontSize: 20, animation: 'pulse 1.2s infinite' }}>🎥</span>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: '#f8fafc',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={treadmillName}
              >
                {treadmillName}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button
                onClick={() => setIsMinimized(m => !m)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  fontSize: 14,
                  padding: '2px 6px',
                  borderRadius: 4,
                }}
                title={isMinimized ? 'Развернуть' : 'Свернуть'}
              >
                {isMinimized ? '□' : '−'}
              </button>
              <button
                onClick={cancelExport}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#ef4444',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 700,
                  padding: '2px 6px',
                  borderRadius: 4,
                }}
                title="Отменить экспорт"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Expanded view details */}
          {!isMinimized && (
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 12, color: '#cbd5e1', display: 'flex', justifyContent: 'space-between' }}>
                <span>{progress?.statusText || 'Обработка...'}</span>
                <span style={{ fontWeight: 700, color: '#818cf8' }}>{progress?.percent ?? 0}%</span>
              </div>

              {/* Progress bar */}
              <div style={{ width: '100%', height: 6, background: 'rgba(255,255,255,0.12)', borderRadius: 3, overflow: 'hidden' }}>
                <div
                  style={{
                    height: '100%',
                    width: `${progress?.percent ?? 0}%`,
                    background: 'linear-gradient(90deg, #6366f1, #10b981)',
                    transition: 'width 0.2s linear',
                    borderRadius: 3,
                  }}
                />
              </div>

              <div style={{ fontSize: 11, color: '#64748b', textAlign: 'center', marginTop: 2 }}>
                ⚡ Вы можете закрыть тренажер и продолжать работу!
              </div>
            </div>
          )}
        </div>
      )}
    </TreadmillExportContext.Provider>
  );
}

export function useTreadmillExport() {
  const ctx = useContext(TreadmillExportContext);
  if (!ctx) {
    throw new Error('useTreadmillExport must be used within a TreadmillExportProvider');
  }
  return ctx;
}
